import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "../lib/api";
import ServiceIcon from "../components/ServiceIcon";
import { ArrowUpRight, Sparkles, Star } from "lucide-react";

const TESTIMONIALS = [
  { name: "Maya Chen", role: "Founder, Nova Finance", quote: "They delivered a brand and product site that actually converts. 42% more sign-ups in 8 weeks." },
  { name: "Daniel Osei", role: "CMO, Maple Coffee", quote: "Our launch sold out in 11 days. The Creative Tribe team is a growth engine with great taste." },
  { name: "Priya Raman", role: "Partner, Beacon Legal", quote: "Professional, strategic, and genuinely creative. We finally look the way we serve our clients." },
];

const DEFAULT_LOGOS = ["Nova", "Maple", "Hearth", "Atlas", "Orbit", "Beacon", "Lumen", "Fable"];

export default function HomePage() {
  const { content } = useOutletContext() || {};
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/services"), api.get("/projects")])
      .then(([s, p]) => {
        setServices(s.data);
        setProjects(p.data);
      })
      .catch(() => {});
  }, []);

  const hero = content?.hero;
  const featured = projects.filter((p) => p.featured).slice(0, 3);
  const logos = (content?.clients && content.clients.length > 0) ? content.clients : DEFAULT_LOGOS;

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-60" />
        <div className="absolute inset-0 radial-glow" />
        <div className="relative mx-auto max-w-7xl px-6 pb-28 pt-24 md:pb-36 md:pt-32">
          <div className="max-w-4xl">
            <span
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70 fade-in-up"
              data-testid="hero-eyebrow"
            >
              <Sparkles size={12} className="text-fuchsia-400" />
              {hero?.eyebrow || "Design · Digital · Growth"}
            </span>
            <h1
              className="mt-6 text-5xl font-bold leading-[0.95] tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl fade-in-up"
              data-testid="hero-headline"
            >
              {hero?.headline?.split(" ").slice(0, -1).join(" ") || "We Build Brands"}{" "}
              <span className="gradient-text">
                {hero?.headline?.split(" ").slice(-1)[0] || "Grow."}
              </span>
            </h1>
            <p
              className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70 md:text-xl fade-in-up"
              data-testid="hero-subheadline"
            >
              {hero?.subheadline ||
                "Creative Tribe is your full-service digital agency — crafting distinctive brands, modern websites, and growth-driven marketing for ambitious teams."}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 fade-in-up">
              <Link
                to={hero?.primary_cta_href || "/contact"}
                data-testid="hero-primary-cta"
                className="group relative inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
              >
                <span className="absolute inset-0 rounded-full gradient-bg" />
                <span className="relative">{hero?.primary_cta_label || "Get a Free Consultation"}</span>
                <ArrowUpRight size={16} className="relative transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to={hero?.secondary_cta_href || "/portfolio"}
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white/90 hover:border-white/30 hover:bg-white/10"
              >
                {hero?.secondary_cta_label || "View Our Work"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Logos Marquee */}
      {logos.length > 0 && (
        <section className="border-y border-white/10 bg-[#0B1020]/40 py-8 overflow-hidden" data-testid="logos-marquee">
          <div className="marquee">
            {[...logos, ...logos].map((l, i) => (
              <span key={i} className="text-2xl font-bold tracking-tighter text-white/30 md:text-3xl">
                {l}<span className="text-fuchsia-500">.</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Services */}
      <section className="relative px-6 py-24 md:py-32" data-testid="home-services">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-fuchsia-400">What we do</p>
              <h2 className="mt-3 text-4xl font-bold tracking-tight text-white md:text-5xl">
                Everything you need, <span className="gradient-text">under one roof.</span>
              </h2>
            </div>
            <Link to="/services" className="text-sm text-white/60 hover:text-white" data-testid="home-services-all">
              See all services →
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <div
                key={s.id}
                className="hover-lift group relative overflow-hidden rounded-2xl border border-white/10 bg-[#131B2F] p-8"
                data-testid={`service-card-${s.slug}`}
              >
                <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-fuchsia-500/10 blur-3xl opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="relative">
                  <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                    <ServiceIcon name={s.icon} className="text-fuchsia-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{s.description}</p>
                  <div className="mt-6 text-xs uppercase tracking-[0.2em] text-white/40">
                    {String(i + 1).padStart(2, "0")} / {String(services.length).padStart(2, "0")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured work */}
      <section className="relative px-6 py-24" data-testid="home-featured-work">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-fuchsia-400">Selected work</p>
              <h2 className="mt-3 text-4xl font-bold tracking-tight text-white md:text-5xl">
                Brands we helped <span className="gradient-text">build.</span>
              </h2>
            </div>
            <Link to="/portfolio" className="text-sm text-white/60 hover:text-white" data-testid="home-portfolio-all">
              Browse portfolio →
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
            {featured.map((p, idx) => (
              <Link
                key={p.id}
                to={`/portfolio/${p.slug}`}
                data-testid={`featured-project-${p.slug}`}
                className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#131B2F] hover-lift ${
                  idx === 0 ? "md:col-span-7 aspect-[16/10]" : "md:col-span-5 aspect-[4/3]"
                }`}
              >
                <div className="absolute inset-0">
                  <img
                    src={p.cover_image}
                    alt={p.title}
                    className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1020] via-[#0B1020]/40 to-transparent" />
                </div>
                <div className="relative flex h-full flex-col justify-end p-8">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-300">{p.category}</span>
                  <h3 className="mt-2 text-2xl font-semibold text-white md:text-3xl">{p.title}</h3>
                  <p className="mt-1 max-w-md text-sm text-white/60">{p.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-t border-white/10 px-6 py-24" data-testid="home-testimonials">
        <div className="mx-auto max-w-7xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-fuchsia-400">Kind words</p>
          <h2 className="mt-3 max-w-3xl text-4xl font-bold tracking-tight text-white md:text-5xl">
            Clients who became <span className="gradient-text">friends.</span>
          </h2>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="rounded-2xl border border-white/10 bg-[#131B2F] p-7">
                <div className="flex gap-1 text-orange-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={14} fill="currentColor" />
                  ))}
                </div>
                <p className="mt-4 text-base leading-relaxed text-white/80">"{t.quote}"</p>
                <div className="mt-6">
                  <div className="text-sm font-semibold text-white">{t.name}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-28" data-testid="home-cta">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-3xl border border-white/10 bg-[#131B2F] p-10 md:p-16">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <h3 className="text-3xl font-bold tracking-tight text-white md:text-5xl">
                Have a project in mind? <span className="gradient-text">Let's talk.</span>
              </h3>
              <p className="mt-3 text-white/60">Free 30-minute strategy call. No strings attached.</p>
            </div>
            <Link
              to="/contact"
              data-testid="home-cta-btn"
              className="group relative inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white"
            >
              <span className="absolute inset-0 rounded-full gradient-bg" />
              <span className="relative">Get a Free Consultation</span>
              <ArrowUpRight size={16} className="relative" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
